import { Permission, TenantConfigPlane, UserRole } from '@today/types'
import { Navigate, type RouteObject } from 'react-router-dom'
import { AuthSurfaceRoute } from '@/components/AuthSurfaceRoute'
import { Layout } from '@/components/Layout'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { RequirePermission } from '@/components/RequirePermission'
import { RequireRole } from '@/components/RequireRole'
import { AdminPluginSectionPage, AdminShellPage } from '@/pages/AdminShell'
import { AppSurfacePage } from '@/pages/AppSurface'
import { AuthCallbackPage } from '@/pages/AuthCallback'
import { compliancePageRoutes } from '@/pages/Compliance'
import { contentPageRoutes } from '@/pages/Content'
import { CustomerDetailPage, CustomerFormPage, CustomersListPage } from '@/pages/Customers'
import { DashboardPage } from '@/pages/Dashboard'
import { AccountInactivePage } from '@/pages/errors/AccountInactive'
import { ForbiddenPage } from '@/pages/errors/Forbidden'
import { HttpErrorKind, HttpErrorPage } from '@/pages/errors/HttpError'
import { NoAccessPage } from '@/pages/errors/NoAccess'
import { RouteErrorPage } from '@/pages/errors/RouteError'
import { UnauthorizedPage } from '@/pages/errors/Unauthorized'
import { importsPageRoutes } from '@/pages/Imports'
import { InvitationCompletePage } from '@/pages/InvitationComplete'
import { InvitationFirstLoginPage } from '@/pages/InvitationFirstLogin'
import { LoginPage } from '@/pages/Login'
import { MfaEnrolPage } from '@/pages/MfaEnrol'
import { NotFoundPage } from '@/pages/NotFound'
import { notificationTemplatesRoute } from '@/pages/NotificationTemplates'
import { offerPageRoutes } from '@/pages/Offers'
import { OnboardingPage } from '@/pages/Onboarding'
import { platformBrandRoute } from '@/pages/PlatformBrand'
import { localeSettingsRedirectRoute, localeSettingsRoute, productPageRoutes } from '@/pages/Products'
import { realtimeDiagnosticsRoute } from '@/pages/RealtimeDiagnostics'
import { SettingsPage } from '@/pages/Settings'
import { MfaEnrolmentCallbackPage } from '@/pages/Settings/MfaEnrolmentCallback'
import { TenantConfigPage } from '@/pages/TenantConfig'
import { TenantLoginPage } from '@/pages/TenantLogin'
import { TenantSsoPage } from '@/pages/TenantSso'
import { attributeMappingRoute } from '@/pages/TenantSso/routes'
import { PlatformShell } from '@/shells/PlatformShell'
import { ShellSessionProvider } from '@/shells/session/ShellSessionProvider'
import { TenantAdminShell } from '@/shells/TenantAdminShell'
import { WorkspaceShell } from '@/shells/WorkspaceShell'

const topLevelRoutes: RouteObject[] = [
	{
		path: '/login',
		element: <LoginPage />,
	},
	// TO-308: a tenant's own login URL — native or its SAML IdP, as the tenant's
	// stored federation status selects. Public by definition.
	{
		path: '/t/:slug/login',
		element: <TenantLoginPage />,
	},
	// TO-309: the invitation email's link. Public — the link token (in the URL
	// fragment) is the credential; a federated user leaves for their SAML IdP.
	{
		path: '/t/:slug/invitation',
		element: <InvitationFirstLoginPage />,
	},
	{
		path: '/auth/callback',
		element: <AuthCallbackPage />,
	},
	{
		path: '/error/inactive',
		element: <AccountInactivePage />,
	},
	{
		path: '/error/unauthorized',
		element: <UnauthorizedPage />,
	},
	// TO-361: a callback refused because the identity has no usable account.
	{
		path: '/error/no-access',
		element: <NoAccessPage />,
	},
	{
		path: '/error/forbidden',
		element: <ForbiddenPage />,
	},
	// TO-640: the rest of the HTTP error-page family. Standalone, so none offers "Try again".
	{ path: '/error/bad-request', element: <HttpErrorPage kind={HttpErrorKind.BadRequest} /> },
	{ path: '/error/not-found', element: <NotFoundPage /> },
	{ path: '/error/server-error', element: <HttpErrorPage kind={HttpErrorKind.ServerError} /> },
	{ path: '/error/unavailable', element: <HttpErrorPage kind={HttpErrorKind.Unavailable} /> },
	{ path: '/error/unexpected', element: <HttpErrorPage kind={HttpErrorKind.Unexpected} /> },
	// TO-637 (AD-4): the authenticated auth surfaces — outside every Layout, so
	// no sidebar; each page draws its own frame. Authenticated, never role-gated
	// here (a pending super-admin must reach enrolment; onboarding gates itself).
	{
		element: <AuthSurfaceRoute />,
		children: [
			// TO-591: the forced-enrolment entry — the target of the `ENROL_MFA`
			// first-login destination and of a `403 MFA_ENROLMENT_REQUIRED` on any
			// other surface. It marks the enrolment forced and starts the same OAuth
			// flow Settings uses.
			{ path: 'auth/mfa/enrol', element: <MfaEnrolPage /> },
			// TO-309: the federated first login's return leg.
			{ path: 't/:slug/invitation/complete', element: <InvitationCompletePage /> },
			// TO-190: the onboarding wizard a first-time admin lands on.
			{ path: 't/:slug/onboarding', element: <OnboardingPage /> },
		],
	},
	// TO-636: the workspace (sell-flow) plane mounts in its own shell (AD-4).
	{
		path: '/',
		element: (
			<ProtectedRoute>
				<WorkspaceShell />
			</ProtectedRoute>
		),
		children: [
			{
				index: true,
				element: <Navigate to="/dashboard" replace />,
			},
			{
				path: 'dashboard',
				element: <DashboardPage />,
			},
			// TO-418: customer management — permission-gated (customer.read / customer.write),
			// not role-gated; without the permission the Forbidden page renders.
			{
				path: 'customers',
				element: (
					<RequirePermission permission={Permission.CUSTOMER_READ}>
						<CustomersListPage />
					</RequirePermission>
				),
			},
			{
				path: 'customers/new',
				element: (
					<RequirePermission permission={Permission.CUSTOMER_WRITE}>
						<CustomerFormPage />
					</RequirePermission>
				),
			},
			{
				path: 'customers/:id',
				element: (
					<RequirePermission permission={Permission.CUSTOMER_READ}>
						<CustomerDetailPage />
					</RequirePermission>
				),
			},
			{
				path: 'customers/:id/edit',
				element: (
					<RequirePermission permission={Permission.CUSTOMER_WRITE}>
						<CustomerFormPage />
					</RequirePermission>
				),
			},
			// TO-416: product catalog admin — permission-gated (product.read / product.write).
			...productPageRoutes,
			...contentPageRoutes,
			// Offers: list, wizard shell and send-progress detail (TO-466, offer.read / offer.write),
			// and Wizard Step 2 (TO-123, offer.write + product.read).
			...offerPageRoutes,
			{
				path: 'settings',
				element: (
					// The ordinal floor for "an admin of some kind". Pinning this to
					// SUPER_ADMIN would lock out PLATFORM_ADMIN and ORG_ADMIN, which sit
					// below it on the coarse ladder yet legitimately administer. The
					// screen's own actions gate on the permissions they require.
					<RequireRole minRole={UserRole.ADMIN}>
						<SettingsPage />
					</RequireRole>
				),
			},
			// TO-63 / TO-591: where the TOTP enrolment sign-in returns. Authenticated
			// only — NOT role-gated: a forced-enrolment user (a pending super-admin
			// holding no privileged role yet) must reach it, and the server authorises
			// setup/verify on its own. Enrolment is open to any authenticated user.
			{
				path: 'settings/security/mfa/callback',
				element: <MfaEnrolmentCallbackPage />,
			},
		],
	},
	// Per-surface entry points. These are protected, so an unauthenticated visit
	// redirects to /login carrying the surface path as `from`, and the user lands
	// back here after the (single-client) Cognito hosted-UI login.
	{
		path: '/admin',
		// W1-2 (TO-634): the platform plane renders inside PlatformShell (AD-4), not the
		// shared Layout. Children, guards and pages are unchanged — only the plane chrome.
		// W1-6 (TO-638): ShellSessionProvider derives the context band from the route +
		// viewer role (step only; enter/actas blocked on AD-5). It reads useLocation, so it
		// sits inside the router here, around the shell.
		element: (
			<ProtectedRoute>
				<ShellSessionProvider>
					<PlatformShell />
				</ShellSessionProvider>
			</ProtectedRoute>
		),
		children: [
			// TO-189: the admin shell — the sections the viewer may open, plugin sections last.
			{ index: true, element: <AdminShellPage /> },
			{ path: 'plugins/:code', element: <AdminPluginSectionPage /> },
			// TO-468: the operator's realtime-channel smoke test — SuperAdmin only.
			realtimeDiagnosticsRoute,
			// TO-232: GDPR deletions + access requests — compliance.read, actions on their manage permissions.
			...compliancePageRoutes,
			// TO-541: the platform-brand switcher — SuperAdmin only.
			platformBrandRoute,
			// TO-416: the "Locale settings" navigation entry — resolves the viewer's slug.
			localeSettingsRedirectRoute,
			// TO-308: the Super-Admin's operator setup path — attach a tenant's SAML
			// metadata before it has an admin of its own. Same API as the tenant plane;
			// the server enforces tenant.manage_idp + tenant.manage.
			{
				path: 'tenants/:slug/sso',
				element: (
					<RequireRole minRole={UserRole.ADMIN}>
						<TenantSsoPage />
					</RequireRole>
				),
			},
			// TO-377: a provider's attribute & role mapping.
			attributeMappingRoute('tenants/:slug/sso/providers/:providerId/attribute-mapping'),
			// TO-374: the tenant configuration console, platform plane — any tenant by id.
			// The server admits the SUPER_ADMIN role only and returns the section manifest.
			{
				path: 'tenants/:tenantId/config/:section?',
				element: (
					<RequireRole anyOf={[UserRole.SUPER_ADMIN]}>
						<TenantConfigPage plane={TenantConfigPlane.PLATFORM} />
					</RequireRole>
				),
			},
		],
	},
	// TO-308: the Tenant Admin's SSO page for their own tenant. The server refuses
	// any other tenant (403 CROSS_TENANT_DENIED) and callers without tenant.manage_idp.
	{
		path: '/t/:slug/admin',
		// W1-3 (TO-635): the tenant-admin plane renders inside TenantAdminShell (AD-4),
		// not the shared Layout. Children, guards and pages are unchanged — only the
		// plane chrome. There is no index route yet (Tenant Overview is TO-672).
		element: (
			<ProtectedRoute>
				<TenantAdminShell />
			</ProtectedRoute>
		),
		children: [
			// TO-416: tenant locale settings (tenant.manage_settings).
			localeSettingsRoute,
			// TO-140: the offer notification-template library (notification_templates.manage).
			notificationTemplatesRoute,
			// TO-399: import monitoring (import_execution.read).
			...importsPageRoutes,
			{
				path: 'sso',
				element: (
					<RequireRole minRole={UserRole.ADMIN}>
						<TenantSsoPage />
					</RequireRole>
				),
			},
			// TO-377: a provider's attribute & role mapping.
			attributeMappingRoute('sso/providers/:providerId/attribute-mapping'),
			// TO-374: the tenant configuration console, tenant plane — the caller's own
			// tenant only. The server refuses any other slug (403 CROSS_TENANT_FORBIDDEN).
			{
				path: 'config/:section?',
				element: (
					<RequireRole minRole={UserRole.ORG_ADMIN}>
						<TenantConfigPage plane={TenantConfigPlane.TENANT} />
					</RequireRole>
				),
			},
		],
	},
	{
		path: '/app',
		element: (
			<ProtectedRoute>
				<Layout />
			</ProtectedRoute>
		),
		children: [{ index: true, element: <AppSurfacePage /> }],
	},
	{
		path: '*',
		element: <NotFoundPage />,
	},
]

/**
 * Gives a top-level route the app's error boundary (TO-640 http-error-pages
 * REQ-003). Errors bubble to the nearest boundary, so one per top-level route
 * covers every nested child, and the card renders in place at the failing URL.
 */
export function withRouteErrorBoundary(route: RouteObject): RouteObject {
	return { ...route, errorElement: <RouteErrorPage /> }
}

export const routes: RouteObject[] = topLevelRoutes.map(withRouteErrorBoundary)
